import fetchWithCsrf from 'js/utils/fetch_with_csrf';
import routes from 'js/core_pre_webpack/routes';

import { Option } from '../SearchBox';
import { SearchType } from '../KiiTypeahead';

export type KiiData = {
  option: Option;
  rawData: string[];
};

export type RawInputData = {
  input: string[];
};

type CoauthorData = {
  display_name: string;
  coauthor_type: string | null;
  coauthor_id: number | null;
  raw_input_data: string[];
};

type PaperTitleData = {
  title: string;
  publication_type: string | null;
  publication_id: number | null;
  raw_input_data: string[];
};

export type KiiResponse = {
  tagging_ids: number[];
  tagging_matches?: boolean[];
};

export type KiiSource =
  | 'spider_2024_q4_liswp_kii_v1'
  | 'sutd'
  | 'spider_2024_q4_dist_sys_kii_v1';

export const postRawInputData = (data: RawInputData[], source: KiiSource) => {
  const route = routes.kii_raw_input_api_v0_user_taggings_user_taggings_path({
    subdomain_param: 'api',
    data: encodeURIComponent(JSON.stringify(data)),
    source,
  });

  return fetchWithCsrf(route, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
};

export const postKiiData = (
  type: SearchType,
  data: KiiData[],
  source: KiiSource,
): Promise<KiiResponse> => {
  if (type === SearchType.CoauthorConstrained) {
    const mappedData: CoauthorData[] = data.map((entry) => {
      let coauthor_type: string | null = 'oa_author';
      let coauthor_id: number | null = entry.option.id;

      if (entry.option.id === null) {
        coauthor_type = null;
        coauthor_id = null;
      }

      return {
        display_name: entry.option.label,
        raw_input_data: entry.rawData,
        coauthor_type,
        coauthor_id,
      };
    });

    return postCoauthorData(mappedData, source);
  }

  if (
    type === SearchType.PaperTitleConstrained ||
    type === SearchType.PaperTitleAll
  ) {
    const mappedData: PaperTitleData[] = data.map((entry) => {
      let publication_type: string | null = 'oa_work';
      let publication_id: number | null = entry.option.id;

      if (entry.option.id === null) {
        publication_type = null;
        publication_id = null;
      }

      return {
        title: entry.option.label,
        raw_input_data: entry.rawData,
        publication_type,
        publication_id,
      };
    });

    return postPaperTitleData(mappedData, source);
  }

  return Promise.reject(`Invalid type: ${type}`);
};

const postCoauthorData = async (data: CoauthorData[], source: KiiSource) => {
  const route = routes.kii_coauthor_api_v0_user_taggings_user_taggings_path({
    subdomain_param: 'api',
    data: encodeURIComponent(JSON.stringify(data)),
    source,
  });

  const resp = await fetchWithCsrf(route, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
  return await resp.json();
};

const postPaperTitleData = async (
  data: PaperTitleData[],
  source: KiiSource,
) => {
  const route = routes.kii_paper_title_api_v0_user_taggings_user_taggings_path({
    subdomain_param: 'api',
    skip_topic_verification: '1',
    data: encodeURIComponent(JSON.stringify(data)),
    source,
  });

  const resp = await fetchWithCsrf(route, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
  return await resp.json();
};
